import React, { useState, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Dropdown, Button, Popconfirm, Tooltip } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';

import useStyles from './style';
import TierInputControl from './tier-input-control';

const TierDefineControl = ({ tenancyInstalments, setTenancyInstalments, getTierRenderText }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const tierItemRef = useRef([]);
  const dropdownContainerRef = useRef(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const tierLength = tenancyInstalments.length;

  const handleConfirmRemoveTierClick = idx => {
    setTenancyInstalments(prevVal =>
      prevVal
        .filter((_, index) => index !== idx)
        .map((item, index, arr) => ({
          ...item,
          fromTenancy: index == 0 ? 0 : arr[index - 1].toTenancy,
        }))
        .map((item, index, arr) => ({
          ...item,
          toTenancy: index == arr.length - 1 ? null : arr[index + 1].fromTenancy,
        })),
    );
  };

  const handleDropdownCancel = () => {
    setDropdownVisible(false);
  };

  const handleDropdownConfirm = tenancyTiers => {
    setTenancyInstalments(tenancyTiers);
    setDropdownVisible(false);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.buttonGroup} ref={dropdownContainerRef}>
        <Dropdown
          overlay={
            <TierInputControl
              visible={dropdownVisible}
              tenancyInstalments={tenancyInstalments}
              onCancel={handleDropdownCancel}
              onConfirm={handleDropdownConfirm}
            />
          }
          trigger="click"
          visible={dropdownVisible}
          overlayStyle={{ zIndex: '1000000' }}
          onVisibleChange={setDropdownVisible}
          getPopupContainer={() => dropdownContainerRef.current}
        >
          <Button className="tier--button__set">
            <EditOutlined />
            {t('landlord.payment.long_tail.rent_payment.setting_modal.set_tenancy_tiers')}
          </Button>
        </Dropdown>

        <For each="tier" index="index" of={tenancyInstalments}>
          <div className="tier--button__item">
            <span dangerouslySetInnerHTML={{ __html: getTierRenderText(tier) }} />

            <If condition={tenancyInstalments?.length > 2}>
              <Popconfirm
                title={t('landlord.payment.long_tail.rent_payment.setting_modal.delete_tier_msg')}
                overlayStyle={{
                  width: 236,
                  zIndex: '1000000',
                }}
                okText={t('landlord.payment.long_tail.button.yes')}
                cancelText={t('landlord.payment.long_tail.button.no')}
                onConfirm={() => handleConfirmRemoveTierClick(index)}
              >
                <CloseOutlined className="tier--button__close" />
              </Popconfirm>
            </If>
          </div>
        </For>
      </div>

      <div className={classes.tierChart}>
        <div className={classes.tierChartHeader}>
          <div className="tier--tag tier--tag__start">
            {t('landlord.payment.long_tail.rent_payment.setting_modal.check_in_date')}
          </div>
          <div className="tier--tag tier--tag__end">
            {t('landlord.payment.long_tail.rent_payment.setting_modal.maximum_tenancy')}
          </div>
        </div>

        <div className={classes.tierChartMain}>
          <For each="tier" index="index" of={tenancyInstalments}>
            <div
              className={classes.tierChartLine}
              style={{ width: `calc((100% / ${tierLength}) * ${index + 1})` }}
            ></div>
            <div
              className={classes.tierChartItemWrapper}
              style={{ width: `calc((100% / ${tierLength}) * ${index + 1})` }}
              ref={el => (tierItemRef.current[index] = el)}
            >
              <If condition={index > 0}>
                <div className="tier--index">{index + 1}</div>
              </If>

              <Tooltip
                title={
                  <>
                    <span className="tier--tooltip__index">
                      {t('landlord.payment.long_tail.rent_payment.setting_modal.tier') +
                        ' ' +
                        (index + 1)}
                    </span>
                    <span
                      className="tier--tooltip__range"
                      dangerouslySetInnerHTML={{ __html: getTierRenderText(tier) }}
                    />
                  </>
                }
                placement="top"
                overlayClassName={classes.tierItemTooltip}
                getPopupContainer={() => tierItemRef.current[index]}
              >
                <div
                  className={classnames(classes.tierChartItem, {
                    'tier-chart--item__first': index === 0,
                    'tier-chart--item__middle': index > 0 && index < tierLength - 1,
                    'tier-chart--item__last': index === tierLength - 1,
                  })}
                  style={{
                    width: `calc(100% / ${index + 1})`,
                  }}
                >
                  <span className="tier--item__start"></span>
                  <div className="tier--item__main">
                    <span className="tier--item__index">
                      {t('landlord.payment.long_tail.rent_payment.setting_modal.tier') +
                        ' ' +
                        (index + 1)}
                    </span>
                    <span
                      className="tier--item__range"
                      dangerouslySetInnerHTML={{ __html: getTierRenderText(tier) }}
                    />
                  </div>
                  <span className="tier--item__end"></span>
                </div>
              </Tooltip>
            </div>
          </For>
        </div>
      </div>
    </div>
  );
};

export default TierDefineControl;
